import React, { useState, useEffect } from 'react'
import { Location } from '@reach/router'
import moment from 'moment'

const STORAGE_KEY_REDIRECT = 'global-banner-redirect-dismissed'
const STORAGE_KEY_MAINTENANCE = 'global-banner-maintenance-dismissed'

// Format DD-MM-YYYY from env to "Weekday D MMMM YYYY" (e.g. "Friday 27 February 2026")
function formatMaintenanceDate (dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return ''
  const m = moment(dateStr.trim(), 'DD-MM-YYYY', true)
  if (!m.isValid()) return ''
  return m.format('dddd D MMMM YYYY')
}

// Format time from env (e.g. "10:00" or "22:00") to readable (e.g. "10:00 AM" / "10:00 PM")
function formatMaintenanceTime (timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return ''
  const trimmed = timeStr.trim()
  const m = moment(trimmed, ['HH:mm', 'H:mm', 'h:mm A'], true)
  if (m.isValid()) return m.format('h:mm A')
  return trimmed
}

const ANIMATION_DURATION_MS = 300
const REDIRECT_BANNER_SCROLL_THRESHOLD = 60

const GlobalInfoBanners = () => {
  const [mounted, setMounted] = useState(false)
  const [redirectDismissed, setRedirectDismissed] = useState(true)
  const [maintenanceDismissed, setMaintenanceDismissed] = useState(true)
  const [redirectClosing, setRedirectClosing] = useState(false)
  const [maintenanceClosing, setMaintenanceClosing] = useState(false)
  const [isBannerAtTop, setIsBannerAtTop] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  // Au scroll : ajoute/retire is-top sur .banner-address → top 0 quand scrollY >= seuil
  useEffect(() => {
    if (!mounted || typeof window === 'undefined') return
    const updateBannerClass = () => {
      setIsBannerAtTop(window.scrollY >= REDIRECT_BANNER_SCROLL_THRESHOLD)
    }
    updateBannerClass()
    window.addEventListener('scroll', updateBannerClass, { passive: true })
    return () => window.removeEventListener('scroll', updateBannerClass)
  }, [mounted])

  useEffect(() => {
    if (!mounted || typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const hasRedirect = params.get('redirect') === 'true'
    const maintenanceEnabled = process.env.GATSBY_MAINTENANCE_ENABLED === 'true'
    const savedRedirect = sessionStorage.getItem(STORAGE_KEY_REDIRECT)
    const savedMaintenance = sessionStorage.getItem(STORAGE_KEY_MAINTENANCE)
    if (hasRedirect && savedRedirect !== 'true') setRedirectDismissed(false)
    if (maintenanceEnabled && savedMaintenance !== 'true') setMaintenanceDismissed(false)
  }, [mounted])

  const dismissRedirect = () => {
    setRedirectClosing(true)
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(STORAGE_KEY_REDIRECT, 'true')
      }
      setRedirectDismissed(true)
      setRedirectClosing(false)
    }, ANIMATION_DURATION_MS)
  }

  const dismissMaintenance = () => {
    setMaintenanceClosing(true)
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(STORAGE_KEY_MAINTENANCE, 'true')
      }
      setMaintenanceDismissed(true)
      setMaintenanceClosing(false)
    }, ANIMATION_DURATION_MS)
  }

  if (!mounted) return null

  return (
    <Location>
      {({ location }) => {
        const path = location ? location.pathname || '' : ''
        const hasRedirect = location && new URLSearchParams(location.search || '').get('redirect') === 'true'
        const maintenanceEnabled = process.env.GATSBY_MAINTENANCE_ENABLED === 'true'

        const showRedirectBanner = hasRedirect && !redirectDismissed
        const showMaintenanceBanner = maintenanceEnabled && !maintenanceDismissed
        const showMaintenanceWithAnimation = showMaintenanceBanner || maintenanceClosing
        const showRedirectWithAnimation = showRedirectBanner || redirectClosing

        const maintenanceStart = process.env.GATSBY_MAINTENANCE_START_DATE || ''
        const maintenanceEnd = process.env.GATSBY_MAINTENANCE_END_DATE || ''
        const maintenanceStartTime = process.env.GATSBY_MAINTENANCE_START_TIME || ''
        const maintenanceEndTime = process.env.GATSBY_MAINTENANCE_END_TIME || ''
        const maintenanceStartFormatted = formatMaintenanceDate(maintenanceStart)
        const maintenanceEndFormatted = formatMaintenanceDate(maintenanceEnd)
        const maintenanceStartTimeFormatted = formatMaintenanceTime(maintenanceStartTime)
        const maintenanceEndTimeFormatted = formatMaintenanceTime(maintenanceEndTime)
        const maintenanceDateText = (maintenanceStartFormatted && maintenanceEndFormatted)
          ? `From ${maintenanceStartFormatted}${maintenanceStartTimeFormatted ? ` at ${maintenanceStartTimeFormatted}` : ''} to ${maintenanceEndFormatted}${maintenanceEndTimeFormatted ? ` at ${maintenanceEndTimeFormatted}` : ''}`
          : (maintenanceStartFormatted || maintenanceEndFormatted || 'From Friday 10:00 PM to Sunday 10:00 AM')

        const baseUrl = 'https://www.starship.org.nz'
        const officialUrl = baseUrl + path
        const officialUrlDisplay = path ? `www.starship.org.nz${path}` : 'www.starship.org.nz'

        const isMasterEnv = process.env.GATSBY_CONTENTFUL_ENVIRONMENT === 'master'
        const bannerAddressTopClass = isMasterEnv ? ' banner-address--top-160' : ''
        const bannerAddressStickyClass = isBannerAtTop ? (isMasterEnv ? ' is-top' : ' is-top') : ''
        const isPtp = path.includes('power-to-protect')
        const ptpClass = isPtp ? ' banner-address--ptp' : ''
        const ptpRedirectClass = isPtp ? ' banner-redirect--ptp' : ''

        return (
    <>
      {/* Bannière bleue (haut) = banner-address : top 120px (160px si master), au scroll → is-top 0 (40px si pas master) */}
      {showMaintenanceWithAnimation && (
        <div
          className={`banner-address ${ptpClass}${redirectClosing ? ' banner-address--closing' : ''}${bannerAddressStickyClass}`}
          role="status"
          aria-live="polite"
        >
            <div className="banner-address__inner">
            <div className="banner-address__content">
              <div className="banner-address__title__wrapper">
                <i className="far fa-info-circle banner-address__icon" aria-hidden="true" />
                <p className="banner-address__title">Scheduled Maintenance Notice</p>
              </div>
              <p className="banner-address__text">
                The Starship website will be temporarily unavailable between Friday 27th February 10.00pm to Sunday 29th February 10.00am to allow essential maintenance to take place.
                During this time you will be able to access the website on <a href="https://starship-backup.vercel.app" className="banner-address__url">https://starship-backup.vercel.app</a> - please note down this website address prior to 27 February.
              </p>
            </div>
            <button
              type="button"
              className="banner-address__close"
              onClick={ dismissMaintenance}
              aria-label="Close maintenance notice"
            >
              <i className="far fa-times" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* Bannière blanche (bas) = banner-redirect : entre par le bas, sort par le bas au close */}
      { showRedirectWithAnimation && (
        <div
          className={`banner-redirect${ptpRedirectClass}${maintenanceClosing ? ' banner-redirect--closing' : ''}`}
          role="status"
          aria-live="polite"
        >
          <div className="banner-redirect__inner">
            <div className="banner-redirect__content">
              <strong className="banner-redirect__title">We&apos;ve updated our website address</strong>
              <p className="banner-redirect__text">
                The official URL is now: <a href={officialUrl} className="banner-redirect__url">{officialUrlDisplay}</a> Please update your bookmark for future visits.
              </p>
            </div>
            <button
              type="button"
              className="banner-redirect__close"
              onClick={dismissRedirect}
              aria-label="Close address update notice"
            >
              <i className="far fa-times" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </>
        )
      }}
    </Location>
  )
}

export default GlobalInfoBanners
